import { useCallback, useEffect, useRef, useState } from "react";
import { History, Loader2, RefreshCw, ScanSearch, Wand2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Puts back the callouts, code blocks, split headings and style-stripped HTML
 *  that a full Helpjuice rebuild threw away — only where the words still match,
 *  so a genuine content change is never reverted. */
export default function KbFormattingRestoreCard() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [scan, setScan] = useState(null);
  const [close, setClose] = useState(false);
  const [busy, setBusy] = useState("");
  const [passes, setPasses] = useState(null);
  const [passState, setPassState] = useState(null);
  const timer = useRef(null);
  const isSuper = user?.role === "super_admin";

  const runScan = useCallback(async (quiet = false) => {
    try {
      const { data } = await api.get("/kb/formatting-restore/scan");
      setScan(data);
      return data;
    } catch (e) {
      if (!quiet) toast.error(e.response?.data?.detail || "Scan failed");
      return null;
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kb/formatting-restore");
      setState((prev) => {
        if (prev?.running && !data.running) {
          runScan(true).then((s) => toast.success(
            `Restore finished — ${data.restored || 0} articles put back${
              s?.identical ? `, ${s.identical + (close ? s.close : 0)} still eligible` : ""}`));
        }
        return data;
      });
    } catch {
      /* card just stays quiet */
    }
  }, [runScan, close]);

  const loadPasses = useCallback(async () => {
    try {
      const [scanRes, stateRes] = await Promise.all([
        api.get("/kb/cleanup-passes/scan"),
        api.get("/kb/cleanup-passes"),
      ]);
      setPasses(scanRes.data);
      setPassState(stateRes.data);
      return stateRes.data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => { load(); runScan(true); loadPasses(); }, [load, runScan, loadPasses]);

  // Poll while the cleanup passes are working, then re-count.
  useEffect(() => {
    if (!passState?.running) return undefined;
    const t = setInterval(async () => {
      const st = await loadPasses();
      if (st && !st.running) {
        clearInterval(t);
        toast.success(`Cleanup passes finished — ${st.articles || 0} articles tidied`);
      }
    }, 4000);
    return () => clearInterval(t);
  }, [passState?.running, loadPasses]);

  const doPasses = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Re-run the cleanup passes on ${passes?.total_articles || 0} articles?\n\n`
      + `• Style strip (${passes?.style_strip?.articles || 0}) — removes Helpjuice's inline font, `
      + "colour and spacing junk\n"
      + `• Heading split (${passes?.heading_split?.articles || 0}) — breaks crammed "Heading: body" `
      + "runs into real headings\n"
      + `• Code rescue (${passes?.code_rescue?.articles || 0}) — turns pasted logs into code blocks`
      + "\n\nEach article keeps a revision, so any one of them can be rolled back.")) return;
    setBusy("passes");
    try {
      await api.post("/kb/cleanup-passes/run");
      toast.success("Cleanup passes started");
      await loadPasses();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the passes");
    } finally { setBusy(""); }
  };

  useEffect(() => {
    if (!state?.running) {
      if (timer.current) { clearInterval(timer.current); timer.current = null; }
      return undefined;
    }
    timer.current = setInterval(load, 4000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [state?.running, load]);

  const eligible = (scan?.identical || 0) + (close ? scan?.close || 0 : 0);

  const doRun = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Restore formatting on ${eligible} articles?\n\n`
      + "Each one goes back to the version saved just before the rebuild, so callouts, code "
      + "blocks, split headings and the tidied HTML come back. Images stay on our own storage.\n\n"
      + (close
        ? "You've included the near-matches, where the wording moved by up to 2%.\n\n"
        : "")
      + "Every article is snapshotted first, so this is reversible per article.")) return;
    setBusy("run");
    try {
      await api.post("/kb/formatting-restore/run", { include_close: close });
      toast.success("Restore started — progress updates here");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the restore");
    } finally { setBusy(""); }
  };

  const pct = state?.total ? Math.round((100 * (state.progress || 0)) / state.total) : 0;

  return (
    <Card className="p-4" data-testid="kb-formatting-restore-card">
      <div className="mb-1 flex items-center gap-2">
        <History className="h-4 w-4 text-slate-500 dark:text-slate-400" />
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          Restore formatting lost in a rebuild
        </div>
      </div>
      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
        A full Helpjuice import replaces every article with the raw export, which throws away
        callouts, code blocks, split headings and the tidied-up HTML. This puts each article back to
        the version saved just before that rebuild — but only where the words still match, so
        nothing anyone genuinely rewrote gets reverted.
      </p>

      {scan && (
        <div className="mb-3 rounded border border-slate-200 dark:border-slate-800 p-2 text-xs"
             data-testid="kb-formatting-restore-scan">
          {scan.rebuild_at ? (
            <>
              <div className="text-slate-700 dark:text-slate-200">
                <b>{scan.identical}</b> articles match word for word
                {" · "}<b>{scan.close}</b> within 2%
                {" · "}<b>{scan.differs}</b> changed too much to touch
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                {scan.no_snapshot} have no snapshot from before the rebuild ·{" "}
                {scan.nothing_to_gain} were never cleaned up in the first place ·{" "}
                last rebuild {String(scan.rebuild_at).slice(0, 16).replace("T", " ")}
              </div>
            </>
          ) : (
            <div className="text-slate-600 dark:text-slate-300">{scan.note}</div>
          )}
        </div>
      )}

      {state?.running && (
        <div className="mb-3" data-testid="kb-formatting-restore-progress">
          <div className="mb-1 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            <Loader2 className="h-3 w-3 animate-spin" />
            {state.progress || 0} / {state.total || "?"} articles restored
          </div>
          <div className="h-1.5 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {!state?.running && state?.finished && (
        <div className="mb-3 text-xs text-slate-600 dark:text-slate-300"
             data-testid="kb-formatting-restore-last">
          Last run finished {String(state.finished).slice(0, 16).replace("T", " ")} ·{" "}
          {state.restored || 0} articles restored · {state.skipped_differs || 0} left alone
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="outline" onClick={async () => {
          setBusy("scan");
          const d = await runScan();
          if (d) toast.success(`${d.identical} exact, ${d.close} near matches`);
          setBusy("");
        }} disabled={!!busy} data-testid="kb-formatting-restore-scan-btn">
          {busy === "scan" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            : <ScanSearch className="mr-1 h-3.5 w-3.5" />}
          Scan
        </Button>
        <Button size="sm" onClick={doRun}
                disabled={!!busy || !isSuper || state?.running || !eligible}
                data-testid="kb-formatting-restore-run-btn">
          {busy === "run" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
          Restore {eligible || ""} articles
        </Button>
        <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
          <Switch checked={close} onCheckedChange={setClose}
                  data-testid="kb-formatting-restore-close-toggle" />
          Include near-matches (wording moved up to 2%)
        </label>
      </div>

      <div className="mt-4 border-t border-slate-200 dark:border-slate-800 pt-3">
        <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
          Re-run the cleanup passes
        </div>
        <p className="mb-2 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          The last tidy-up of each article was never snapshotted, so a restore can&apos;t bring it
          back. These three passes are deterministic — running them again reproduces the work.
        </p>
        {passes && (
          <div className="mb-2 font-mono text-[11px] text-slate-500 dark:text-slate-400"
               data-testid="kb-cleanup-passes-scan">
            {passes.style_strip?.articles} style strip · {passes.heading_split?.articles} heading
            split · {passes.code_rescue?.articles} code rescue
          </div>
        )}
        {passState?.running ? (
          <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
               data-testid="kb-cleanup-passes-progress">
            <Loader2 className="h-3 w-3 animate-spin" />
            {passState.pass || "starting"} · {passState.pass_done || 0} / {passState.pass_total || "?"}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="outline" onClick={doPasses}
                    disabled={!!busy || !isSuper || !passes?.total_articles}
                    data-testid="kb-cleanup-passes-run-btn">
              {busy === "passes" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Wand2 className="mr-1 h-3.5 w-3.5" />}
              Tidy {passes?.total_articles || ""} articles
            </Button>
            {passState?.finished && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400"
                    data-testid="kb-cleanup-passes-last">
                last run {String(passState.finished).slice(0, 16).replace("T", " ")} ·{" "}
                {passState.articles || 0} tidied
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
